import type {Where} from 'payload'

import {resolveRelation, resolveRelations} from '~/utils/relations'
import {extractDocs} from '~/utils/transform-payload'

export function useIndustryProjects() {
  const sdk = usePayloadSdk()

  const fetch = async (slug: string) => {
    // First, try to find an industry with this slug
    const industryResponse = await sdk.find({
      collection: 'industries',
      where: {
        slug: {equals: slug},
        _status: {equals: 'published'},
      },
      limit: 1,
    })

    const industries = extractDocs(industryResponse)
    const industry = industries[0]

    if (industry) {
      // Industry exists, fetch projects for this industry
      const where: Where = {
        and: [
          {_status: {equals: 'published'}},
          {
            industries: {
              in: [industry.id],
            },
          },
        ],
      }

      const projectsResponse = await sdk.find({
        collection: 'projects',
        depth: 2,
        where,
        sort: '-createdAt',
        limit: 1000,
      })

      const projects = extractDocs(projectsResponse).map(item => ({
        ...item,
        industries: resolveRelations(item.industries).map(ind => ind.name),
        industriesSlugs: resolveRelations(item.industries).map(ind => ind.slug),
        countryName: resolveRelation(item.country)?.countryName,
      }))

      return {
        industry,
        projects,
      }
    }

    // No industry found, try to find a project with this slug
    const projectResponse = await sdk.find({
      collection: 'projects',
      where: {
        slug: {equals: slug},
        _status: {equals: 'published'},
      },
      depth: 2,
      limit: 1,
    })

    const projects = extractDocs(projectResponse)
    const project = projects[0]

    if (project) {
      // Project exists - return it without an industry for redirect handling
      const processedProject = {
        ...project,
        industries: resolveRelations(project.industries).map(ind => ind.name),
        industriesSlugs: resolveRelations(project.industries).map(ind => ind.slug),
        countryName: resolveRelation(project.country)?.countryName,
      }

      return {
        industry: undefined,
        projects: [processedProject],
      }
    }

    // Neither industry nor project found
    return undefined
  }

  return {fetch}
}
